/**
 * queue/activeQueue.tsx
 * Which queue the live surfaces read/write. The in-memory store is a single
 * hardcoded demo queue with no real id; live reads need a concrete queueId, so
 * this context lists the org's queues (GET /v1/queues) and tracks the selected
 * one. The topbar picker drives setActiveQueue; screens read activeQueueId.
 *
 * Only active in 'live' mode — in 'memory' mode there is nothing to list and the
 * screens use the in-memory store as before, so we skip the fetch entirely.
 *
 * Selection is persisted per-org (AsyncStorage/localStorage) so a reload keeps
 * the operator on the same queue. On first load (or if the saved id is gone) we
 * fall back to the first queue in the list.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { listQueues } from '../services/queue/queueService';
import { handleApiError } from '../utils/errorHandler';
import { useDataSource } from './dataSource';
import { useSession } from '../context/sessionContext';
import AsyncStorage from '../storage/asyncStorageWeb';
import type { QueueSummary } from '../api/queueDtos';

interface ActiveQueueValue {
  queues: QueueSummary[];
  activeQueueId: string | null;
  activeQueue: QueueSummary | null;
  setActiveQueue: (id: string) => void;
  loading: boolean;
  reload: () => void;
}

const ActiveQueueContext = createContext<ActiveQueueValue | null>(null);

/** Per-org storage key so switching org doesn't carry a stale queue selection. */
const storageKey = (orgId: string | undefined) => `skq.activeQueue.${orgId ?? 'default'}`;

export function ActiveQueueProvider({ children }: { children: React.ReactNode }) {
  const { mode } = useDataSource();
  const { org, isAuthenticated } = useSession();
  const [queues, setQueues] = useState<QueueSummary[]>([]);
  const [activeQueueId, setActiveQueueId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const setActiveQueue = useCallback((id: string) => {
    setActiveQueueId(id);
    // Persist so a reload keeps the operator on the same queue.
    void AsyncStorage.setItem(storageKey(org?.id), id);
  }, [org?.id]);

  const reload = useCallback(() => {
    // Nothing to list unless we're live and signed in.
    if (mode !== 'live' || !isAuthenticated) {
      setQueues([]);
      setActiveQueueId(null);
      return;
    }
    setLoading(true);
    listQueues()
      .then(async ({ queues: list }) => {
        setQueues(list);
        if (list.length === 0) { setActiveQueueId(null); return; }
        // Prefer the persisted selection; fall back to the first queue.
        const saved = await AsyncStorage.getItem(storageKey(org?.id));
        const pick = (saved && list.some((q) => q.id === saved)) ? saved : list[0].id;
        setActiveQueueId(pick);
      })
      .catch((err) => handleApiError(err, 'Could not load queues'))
      .finally(() => setLoading(false));
  }, [mode, isAuthenticated, org?.id]);

  useEffect(() => { reload(); }, [reload]);

  const activeQueue = queues.find((q) => q.id === activeQueueId) ?? null;

  return (
    <ActiveQueueContext.Provider
      value={{ queues, activeQueueId, activeQueue, setActiveQueue, loading, reload }}
    >
      {children}
    </ActiveQueueContext.Provider>
  );
}

export function useActiveQueue(): ActiveQueueValue {
  const ctx = useContext(ActiveQueueContext);
  if (!ctx) throw new Error('useActiveQueue must be used within ActiveQueueProvider');
  return ctx;
}
